<template>
  <div class="lkp__multiselect--wrapper">
    <ValidationProvider
      v-slot="v"
      :name="field.label"
      :rules="rules"
      :custom-messages="ruleMessages"
      tag="div"
    >
      <label
        v-if="isLabelVisible"
        class="lkp-field__label"
      >
        {{ field.label }}
        <span 
          v-if="v.required"
          class="text-danger"
        >*</span>
        <span 
          v-if="validationWarnings?.length"
          class="ml-1 cursor-pointer"
        >
          <font-awesome-icon
            :icon="['fas', 'circle-xmark']"
            size="lg"
            class="text-danger"
            data-toggle="tooltip"
            title="Validation Failed"
          />
        </span>
        <span 
          v-if="dataQualityIssues?.length"
          class="ml-1 cursor-pointer"
        >
          <font-awesome-icon
            :icon="['fas', 'circle-info']"
            size="lg"
            class="text-warning"
            data-toggle="tooltip"
            title="Data Quality Issue"
          />
        </span>
      </label>
      <div>
        <ul class="small m-0">
          <li 
            v-for="valItem in validationWarnings"
            class="my-1 p-1 alert alert-danger"
          >
            <strong>{{ valItem.warning }}</strong><span v-if="valItem.description"> - {{ valItem.description }}</span>
          </li>
        </ul>
      </div>
      <div>
        <ul class="small m-0">
          <li 
            v-for="valItem in dataQualityIssues"
            class="my-1 p-1 alert alert-warning"
          >
            <strong>{{ valItem.warning }}</strong><span v-if="valItem.description"> - {{ valItem.description }}</span>
          </li>
        </ul>
      </div>
      <multiselect
        :ref="`${transitRef}-${field.name}-${index + 1}`"
        v-model="value"
        :data-multiselect="getMultiselectId"
        :options="options"
        placeholder="Type to search"
        label="text"
        track-by="id"
        :class="['lkp__multiselect', { 'is-invalid': v.errors.length && !hideErrorMsg }]"
        :close-on-select="!isFilterDropdown"
        :clear-on-select="!isFilterDropdown"
        :searchable="true"
        :max-height="350"
        :disabled="disabled"
        :show-labels="false"
        :loading="isLoading"
        :show-no-results="true"
        :show-no-options="false"
        :group-values="isSuggestionsExist ? 'items' : ''"
        :group-label="isSuggestionsExist ? 'title' : ''"
        :open-direction="getOpenDirection"
        :multiple="isFilterDropdown"
        :state="getState(v)"
        @search-change="findLkps"
        @open="findLkps"
        @remove="onRemove"
        @close="onClose"
        @select="onSelect"
        @blur="onBlur"
      >
        <template #option="props">
          <span v-if="props.option.$isLabel">
            {{ props.option.$groupLabel }}
          </span>
          <span
            v-else
            :id="`multiselect-option-${props.option.text}`"
          >
            {{ props.option.text }}
          </span>
        </template>
        <template
          v-if="!isLoading && (options.length === 50 || options.length === 51)"
          #afterList
        >
          <div class="footer__element">
            The list above displays the first 50 lookup values. If you're looking for a specific value,
            please enter populate search box to find them.
          </div>
        </template>
        <template
          v-if="!isValidationRules && !isLoading && !!options.length && !isFilterDropdown"
          #beforeList
        >
          <b-btn
            size="sm"
            class="add-lkp__btn"
            variant="light"
            @click="onAddNewRelationalEntity"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />
            <span>{{ getBtnTitle }}</span>
          </b-btn>
        </template>
        <template #noResult>
          <p class="no-result">
            No elements found
          </p>
        </template>
      </multiselect>
      <b-form-invalid-feedback
        v-if="!hideErrorMsg"
        :state="getState(v)"
      >
        {{ getError(v.errors) }}
      </b-form-invalid-feedback>
    </ValidationProvider>
  </div>
</template>

<script>
import {debounce} from 'lodash';
import {createNamespacedHelpers} from 'vuex';
import {transformLookupValues} from '@/core/utils/transformLookupValues';
import {axiosClient} from '@/core/services/http.service';
import {getDataTypeJson} from '@/core/utils/getDataTypeJson';
import {lookupMixin} from '@/core/mixins/lookup.mixin';
import { isArray } from 'lodash';
import { extend } from 'vee-validate';

// custom vee-validate rule to check if the value of lookup is <EMPTY>
extend('notEmptyOption', {
  validate: value => {
    return value && value.id !== null && value.text !== '<EMPTY>';
  },
  message: 'This field is required',
});

const {
  mapActions: dataManagementMapActions,
} = createNamespacedHelpers('dataManagement');

const {
  mapState: sidebarMapState,
} = createNamespacedHelpers('sidebar');

const DEFAULT_PARAM = {id: null, text: '<EMPTY>'};

export default {
  name: 'LookupInputComponent',
  mixins:[lookupMixin],
  props: [
    'isValidationRules',
    'isEditModal',
    'isUploadData',
    'isLabelVisible',
    'propsValue',
    'field',
    'ruleIndex',
    'conditionIndex',
    'index',
    'id',
    'disabled',
    'item',
    'transitRef',
    'isTodoList',
    'isEditRowModal',
    'isFilterDropdown',
    'hideErrorMsg',
    'model',
  ],
  data() {
    return {
      data: null,
      value: '',
      isLoading: false,
      options: [],
      relatedTableId: null,
      wasRendered: false,
      rules: '',
      ruleMessages: {
        required: 'This field is required',
      },
      validationWarnings: null,
      dataQualityIssues: null,
    };
  },
  watch: {
    propsValue: {
      handler(newVal) {
        const isNewValueValid = this.isValidPropsValue(newVal);
        if (!newVal || !isNewValueValid) return;       
        if (this.isEditModal || this.isEditRowModal) {
          const ref = this.$refs[`${this.transitRef}-${this.field.name}-${this.index + 1}`];
          if (ref) {
            ref.select(newVal);
          }else{
            this.$nextTick(()=>{
              const ref = this.$refs[`${this.transitRef}-${this.field.name}-${this.index + 1}`];
              ref && ref.select(newVal);               
            });
          }          
        }
      },
      deep: true,
    },
  },
  computed: {
    ...sidebarMapState({
      tablesList: state => state.tablesList,
    }),
    getMultiselectId() {
      return this.isValidationRules
        ? `rule-${this.ruleIndex}-condition-${this.conditionIndex}-expression-${this.index + 1}-value`
        : 'multiselect-input';
    },
    getBtnTitle() {
      return `Add New ${this.tablesList[this.relatedTableId]?.label}`;
    },
    // render dropdown always on bottom, as upwards it can mess with z-index of a section items
    getOpenDirection() {
      return 'bottom';
    },
  },
  methods: {
    ...dataManagementMapActions([
      'getPagedDataLookupValueItems',
      'addSelectedValueToDefaultFields',
      'addRefToStore',
    ]),
    isValidPropsValue(val) { 
      return typeof val === 'object' && val !== null &&
           'id' in val &&
           'text' in val;
    },
    onSelect(selectedOption) {
      if (this.isFilterDropdown) {
        this.$emit('on-select', selectedOption);
      } else if (!this.isValidationRules ) {
        this.options.push(selectedOption);
        this.addSelectedValueToDefaultFields(selectedOption);
        if (!this.propsValue || this.propsValue.id !== selectedOption.id) {
          if (!this.isUploadData && !this.isEditModal) {
            //if it's TableView
            //if it's TodoList too 
            
            this.$emit(
              'on-select',
              selectedOption,
              this.propsValue,
              this.index,
              this.item,
              this.field,
            );
          } else {
            //if it's EditSectionModal
            if (this.isEditModal) {
              this.$emit('on-select', this.field, selectedOption);
            } else {
              //if it's IMPORT/UPLOAD
              this.$emit(
                'on-select',
                this.index,
                this.field,
                this.item,
                selectedOption,
              );
            }
          }

          this.value = selectedOption; 
        }
      } else {
        this.propsValue.value = selectedOption.id;
      }
    },
    onClose(_) {
      this.$emit('on-close', _, this.field);
    },
    onRemove(selectedOption) {
      if(this.isFilterDropdown){
        this.$emit('on-remove', selectedOption);
        return;
      }
      if (this.propsValue.value === selectedOption.id) {
        this.value = selectedOption;
      }
    },
    onAddNewRelationalEntity() {
      this.$emit('on-add-new-relation', this.item, this.index, this.field);
    },
    async getRelatedFieldGuid() {
      const currentJSON = getDataTypeJson(this.field.dataTypeJSON);
      const {body} = await axiosClient.get(`api/field/${currentJSON.FieldToDisplayGuid}`);
      const relatedFieldGuid = body.entityGuid;
      this.field.relationalTableId = relatedFieldGuid;
      this.relatedTableId = relatedFieldGuid;
      this.field.relationalFieldId = currentJSON.FieldToDisplayGuid;
    },
    addSelectedOptions(options, value){
      if(!options.some(option => option.id === value.id)){         
        options.splice(1, 0, value);          
      }
    },
    findLkps: debounce(async function (query) {
      this.isLoading = true;
      if(!this.isFilterDropdown){
        await this.getRelatedFieldGuid();
      }
      
      const {body, success} = await this.getPagedDataLookupValueItems({
        field: this.field,
        query,
        isValidationRules: this.isValidationRules || this.isEditModal,
      });
      if (success) {
        const options = transformLookupValues(body.data, false, body.mdmDataTypeJSON);
        !this.isValidationRules && options.unshift(DEFAULT_PARAM);

        (isArray(this.value))
          ? this.value.forEach(val => this.addSelectedOptions(options, val))
          : this.addSelectedOptions(options, this.value);

        if (this.isSuggestionsExist) {
          this.options = [
            {title: 'Suggested values', items: this.getItemSuggestions},
            {title: 'Other values', items: options},
          ];
        } else {
          this.options = options;
        }
      }
      this.isLoading = false;
      this.$emit('loading-end');
    }, 500),

    async getLkpsValues() {
      if (this.propsValue === null) return;

      const currentCondition = this.isValidationRules || this.isEditModal
        ? this.propsValue.value || this.propsValue.id
        : this.propsValue;

      if (!currentCondition) {
        this.value = DEFAULT_PARAM;
        return;
      }
      if (!this.field) return;

      this.isLoading = true;

      if ((this.isValidationRules || this.isEditModal || this.isTodoList) && this.value.id !== null) {
        if(!this.isFilterDropdown){
          await this.getRelatedFieldGuid();
        }
       
        const {body} = await this.getPagedDataLookupValueItems({
          field: this.field,
          query: this.isValidationRules || this.isEditModal || this.isTodoList
            ? this.propsValue.id || this.propsValue.value || this.propsValue
            : '',
          isValidationRules: true,
        });
        if (this.isValidationRules || this.isEditModal || this.isTodoList) {
          const item = body.data[0];
          if (item) {
            this.value = {
              id: item.recordGuid,
              text: item.value,
            };
          }
        }
      } else {
        await this.$refs[`${this.transitRef}-${this.field.name}-${this.index + 1}`].activate();
        this.value = this.propsValue;
      }
      this.isLoading = false;
    },
    onBlur($event) {
      this.$emit('blur', $event);
    },
    getError(errors) {
      return errors[0];
    },
    getState(validator) {
      // Determines the validation state of the field
      if (validator.validated === true) {
        return validator.valid === true ? null : false;
      }
      if (!validator.touched) {
        return null;
      }
      return validator.valid === true ? null : false;
    },
    getRules() {
      const rules = [];

      if (this.field.required || this.field.isRequired) {
        rules.push('required');
        rules.push('notEmptyOption');
      }
      return rules.join('|');
    },
    // Sort validation warnings and data quality issues into separate arrays
    sortValidationWarnings() {
      this.validationWarnings = [];
      this.dataQualityIssues = [];
      
      if (!this.model || !this.model.failedFields) return;

      for (const f of this.model.failedFields) {
        if (f.fields.includes(this.field.fieldGuid)) {
          if (f.type === 'warning') this.validationWarnings.push(f);
          else if (f.type === 'dataQuality') this.dataQualityIssues.push(f);
        }
      }
    },
  },
  async mounted() {
    await this.getLkpsValues();
    this.rules = this.getRules();
    const name = `${this.transitRef}-${this.field.name}-${this.index + 1}`;
    this.addRefToStore({name, ref: this.$refs[name]});
    if(this.isFilterDropdown){     
      await this.$refs[`${this.transitRef}-${this.field.name}-${this.index + 1}`].activate();
    }
    const $select = document.querySelector(`[data-multiselect="${this.getMultiselectId}"]`);
    if ($select) {
      $select.setAttribute('id', this.getMultiselectId);
    }
    this.sortValidationWarnings();
  },
};
</script>


<style lang="scss">
.lkp__multiselect--wrapper {
  margin-bottom: 10px;
}

.lkp__multiselect {
  .multiselect__tags {
    min-height: 35px;
    padding: 0 40px 0 7px;
    border: 1px solid #ced4da;
    display: flex;
    align-items: center;
  }

  .multiselect__select {
    width: 35px;
    height: 33px;
  }

  .multiselect__input,
  .multiselect__single {
    white-space: nowrap;
    color: #495057;
    margin-bottom: 0;

    &::placeholder {
      font-size: 0.85rem;
      color: #adadad;
      padding-top: 0;
      margin-bottom: 0;
    }
  }

  .multiselect__placeholder {
    font-size: 0.85rem;
    padding-left: 5px;
    padding-top: 0;
    margin-bottom: 0;
  }

  &.multiselect--disabled {
    opacity: 1 !important;
    
    .multiselect__tags,
    .multiselect__select,
    span {
      background: #e9ecef;
      
    }
    .multiselect__single {
      color: #495057 !important;
    }
  }
}

.add-lkp__btn.btn {
  text-align: left;
  width: 100%;

  span {
    margin-left: 5px;
  }
}

.footer__element {
  padding: 10px;
  font-size: 0.8em;
  text-wrap: wrap;
}

.no-result {
  text-wrap: wrap;
}

.lkp-field__label {
  margin-bottom: 0;
}

.lkp__multiselect {
  &.is-invalid {
    .multiselect__tags {
      border-color: #dc3545 !important; 
    }
  }
}

</style>